import React, { useEffect, useState, useMemo } from "react";
import { db } from "@/firebase/app";
import { doc, getDoc } from "firebase/firestore";

import { FpsContext, FpsContextValue } from "./FpsContext";

// Default FPS values - aligned with backend constants
const DEFAULT_FPS = 30;
const DEFAULT_MAX_FPS = 60;

export const FpsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [fps, setFps] = useState<number>(DEFAULT_FPS);
    const [maxFps, setMaxFps] = useState<number>(DEFAULT_MAX_FPS);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        let isMounted = true;

        const fetchMaxFps = async () => {
            try {
                const previewStreamConfigSnapshot = await getDoc(
                    doc(db, "global", "inference_preview_stream")
                );
                const previewStreamConfig = previewStreamConfigSnapshot.data();
                const fetchedMaxFps = previewStreamConfig?.maxFps || DEFAULT_MAX_FPS;

                if (isMounted) {
                    setMaxFps(fetchedMaxFps);
                    // FPS should not exceed maxFps
                    setFps(Math.min(DEFAULT_FPS, fetchedMaxFps));
                    setIsLoading(false);
                }
            } catch (err) {
                console.error("[FpsProvider] Error fetching max FPS from Firestore:", err);
                if (isMounted) {
                    setError(err as Error);
                    setIsLoading(false);
                    // Use defaults on error
                    setFps(DEFAULT_FPS);
                    setMaxFps(DEFAULT_MAX_FPS);
                }
            }
        };

        fetchMaxFps();

        return () => {
            isMounted = false;
        };
    }, []);

    // Memoize context value to prevent unnecessary re-renders
    const contextValue = useMemo<FpsContextValue>(
        () => ({ fps, maxFps, isLoading, error }),
        [fps, maxFps, isLoading, error]
    );

    return <FpsContext.Provider value={contextValue}>{children}</FpsContext.Provider>;
};
